import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Info, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { api, errMsg } from "@/lib/api";
import { useFilters } from "@/context/FilterContext";
import { MODULES } from "@/lib/nav";
import { FilterBar } from "@/components/FilterBar";
import { KpiCard } from "@/components/KpiCard";
import { TrendChart } from "@/components/TrendChart";
import { BreakdownChart } from "@/components/BreakdownChart";
import { DataTable } from "@/components/DataTable";
import { ExportButtons } from "@/components/ExportButtons";
import { InsightCard } from "@/components/InsightCard";
import { StatusDonut } from "@/components/StatusDonut";
import { DendaSelisih } from "@/components/DendaSelisih";
import { TopBottomYO } from "@/components/TopBottomYO";
import { OfficerDialog } from "@/components/OfficerDialog";
import { AnomaliDialog } from "@/components/AnomaliDialog";
import { SpikeAlert } from "@/components/SpikeAlert";
import { RingkasanCharts } from "@/components/RingkasanCharts";
import { EmptyRowsPanel } from "@/components/EmptyRowsPanel";
import { MarketShare } from "@/components/MarketShare";

function Coverage({ c }) {
  if (!c || (!c.new.length && !c.gone.length)) return null;
  return (
    <div data-testid="coverage-note" className="flex gap-3 rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-200">
      <Info className="mt-0.5 h-4 w-4 shrink-0" />
      <div>
        <p className="font-semibold">Cakupan unit berubah dibanding {c.prev_year} ({c.previous} → {c.current} unit)</p>
        {c.new.length > 0 && <p>Unit baru: {c.new.join(", ")}</p>}
        {c.gone.length > 0 && <p>Tidak ada data periode ini: {c.gone.join(", ")}</p>}
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>
      <Skeleton className="h-80 rounded-xl" />
    </div>
  );
}

export default function ModulePage({ module }) {
  const { params, ready, dataVersion, filters, setFilters, metaError } = useFilters();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [katUnit, setKatUnit] = useState(null);
  const [officer, setOfficer] = useState(null);
  const [anomUnit, setAnomUnit] = useState(null);
  const key = JSON.stringify(params);
  const meta = MODULES.find((m) => m.id === module);

  useEffect(() => {
    setKatUnit(null);
  }, [module, key]);

  useEffect(() => {
    if (!ready) return;
    let alive = true;
    setError(null);
    api.get(`/metrics/${module}`, { params, timeout: 60000 }).then((r) => alive && setData(r.data)).catch((e) => alive && setError(errMsg(e)));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, key, ready, dataVersion]);

  const canDrill = !filters.unit && !["produktivitas", "anomali"].includes(module);
  const rowClick = module === "produktivitas" ? setOfficer : module === "anomali" ? (r) => r.unit && setAnomUnit(r.unit)
    : canDrill ? (r) => r.unit && setFilters({ unit: r.unit }) : undefined;
  const rowHint = { produktivitas: "klik baris untuk rincian skor per bulan", anomali: "klik baris UP3 untuk detail laporan RN & LU" }[module]
    || (canDrill ? "klik baris untuk drill-down unit" : undefined);
  const Icon = meta?.icon;

  return (
    <div>
      <FilterBar />
      <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#F5A623]">{Icon && <Icon className="h-3.5 w-3.5" />}Modul Kinerja</p>
            <h1 data-testid="module-title" className="font-heading mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight">{data?.title || meta?.label}</h1>
            <p data-testid="module-period-label" className="mt-1 text-sm text-muted-foreground">
              {data?.period_label}{filters.unit ? ` · ${filters.unit}` : ""}
            </p>
          </div>
          <ExportButtons module={module} extra={katUnit ? { kategori_unit: katUnit } : {}} />
        </div>
        {filters.unit && (
          <button data-testid="clear-unit-chip" onClick={() => setFilters({ unit: null })} className="inline-flex items-center gap-1.5 rounded-full bg-[#0A3967] px-3 py-1 text-xs font-medium text-white">
            Unit: {filters.unit}<X className="h-3 w-3" />
          </button>
        )}
        {!ready && metaError && (
          <div data-testid="meta-error" className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"><AlertCircle className="h-4 w-4" />{metaError}</div>
        )}
        {error && (
          <div data-testid="module-error" className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"><AlertCircle className="h-4 w-4" />{error}</div>
        )}
        {!data && !error && <LoadingState />}
        {data?.empty && <p data-testid="empty-state" className="rounded-xl border bg-card p-8 text-center text-muted-foreground">Belum ada data tersinkron. Admin dapat menekan "Refresh Sekarang".</p>}
        {data && !data.empty && (
          <>
            <InsightCard insights={data.insights} />
            <div className="grid grid-cols-1 gap-4 min-[420px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {data.kpis.map((k, i) => (
                <KpiCard key={k.key} kpi={k} index={i} prevYear={data.prev_year}
                  onClick={module === "ringkasan" && k.module ? () => navigate(`/m/${k.module}`) : undefined} />
              ))}
            </div>
            <Coverage c={data.coverage} />
            <SpikeAlert spikes={data.spikes} />
            <EmptyRowsPanel info={data.empty_rows} />
            <MarketShare share={data.share} />
            <DendaSelisih rows={data.selisih} />
            {module === "ringkasan" ? (
              <RingkasanCharts data={data} showLb={!filters.unit} table={
                <DataTable key={`${module}-${key}`} table={data.table} title="Rincian per Unit" rowHint={rowHint} onRowClick={rowClick} />} />
            ) : (<>
            {(data.trend || data.breakdown) && (
              <div className={`grid gap-6 ${data.trend && data.breakdown ? "xl:grid-cols-5" : ""}`}>
                {data.trend && <div className={data.breakdown ? "xl:col-span-3" : ""}><TrendChart trend={data.trend} /></div>}
                {data.breakdown && <div className={data.trend ? "xl:col-span-2" : ""}><BreakdownChart breakdown={data.breakdown} unit={katUnit} onUnit={setKatUnit} /></div>}
                {data.breakdown2 && <div className="xl:col-span-5"><BreakdownChart breakdown={data.breakdown2} /></div>}
                {data.breakdown?.trend && (
                  <div data-testid="kategori-trend" className="xl:col-span-5">
                    <TrendChart trend={(katUnit && data.breakdown.units?.[katUnit]?.trend) || data.breakdown.trend} />
                  </div>
                )}
              </div>
            )}
            {(data.charts?.length > 0 || data.status_mix) && (
              <div data-testid="extra-charts" className="grid gap-6 xl:grid-cols-2">
                {data.charts?.map((c) => <TrendChart key={c.title} trend={c} />)}
                <StatusDonut mix={data.status_mix} />
              </div>
            )}
            {data.note && <p className="text-xs text-muted-foreground">{data.note}</p>}
            {module === "produktivitas" && <TopBottomYO rows={data.officers} />}
            <DataTable key={`${module}-${key}`} table={data.table} title={module === "produktivitas" ? "Daftar Petugas YO" : "Rincian per Unit"}
              rowHint={rowHint} onRowClick={rowClick} />
            </>)}
            <OfficerDialog officer={officer} onClose={() => setOfficer(null)} />
            <AnomaliDialog unit={anomUnit} params={params} onClose={() => setAnomUnit(null)} />
          </>
        )}
      </div>
    </div>
  );
}
